<script setup>
import { ref, inject } from "vue";
// import QrcodeVue from "qrcode.vue";
import CopyActionButton from "./CopyActionButton.vue";

const props = defineProps({
    checkPayTo: { type: String, default: "" },
    checkMailTo: { type: String, default: "" },
    invoiceEmail: { type: String, default: "" },
    zelleReference: { type: String, default: "" },
    totalAmount: { type: [String, Number], default: "0" },
    copiedKey: { type: String, default: "" },
    copyToClipboard: { type: Function, required: true },
    currencyFormat: { type: Function, required: true },
    layout: { type: String, default: "full" },
});

const contentGridClass = props.layout === "sidebar" ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 md:grid-cols-2 gap-4";

// ── Upload ────────────────────────────────────────────────────────────────────
const axios = inject("axios");
const isMobile = ref(/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
// const checkQrUrl = computed(() => `${window.location.origin}/upload/check`);

const showUpload = ref(false);
const fileInput = ref(null);
const uploadedFile = ref(null);   // { name, raw }
const uploadLoading = ref(false);
const uploadSuccess = ref(false);
const uploadError = ref("");

const onFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadedFile.value = { name: file.name, raw: file };
    uploadError.value = "";
};

const clearFile = () => {
    uploadedFile.value = null;
    if (fileInput.value) fileInput.value.value = "";
};

const confirmUpload = async () => {
    if (!uploadedFile.value?.raw) return;
    uploadLoading.value = true;
    uploadError.value = "";
    try {
        const formData = new FormData();
        formData.append("file", uploadedFile.value.raw);
        formData.append("type", "check");
        if (props.zelleReference) formData.append("reference", props.zelleReference);
        await axios.authApi.post("/upload-payment-proof", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        uploadSuccess.value = true;
    } catch (e) {
        console.error("Check upload failed", e);
        uploadError.value = "Upload failed. Please try again or email us.";
    } finally {
        uploadLoading.value = false;
    }
};

const instructionsCopied = ref(false);
const copyInstructions = () => {
    const lines = [
        `Pay To The Order Of: ${props.checkPayTo}`,
        `Mail To: ${props.checkMailTo}`,
        props.zelleReference ? `Amount: $${props.currencyFormat(props.totalAmount)}` : null,
        props.zelleReference ? `Memo: ${props.zelleReference}` : null,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(lines).catch(() => {});
    instructionsCopied.value = true;
    setTimeout(() => { instructionsCopied.value = false; }, 2000);
};

const resetUpload = () => {
    showUpload.value = false;
    uploadedFile.value = null;
    uploadLoading.value = false;
    uploadSuccess.value = false;
    uploadError.value = "";
    if (fileInput.value) fileInput.value.value = "";
};
// ─────────────────────────────────────────────────────────────────────────────
</script>

<template>
    <div class="space-y-2">
        <div
            class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3">
            <div>
                <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Pay To The Order Of
                </p>
                <p class="text-theme-secondary text-sm font-normal mt-0.5">{{ props.checkPayTo }}</p>
            </div>
            <CopyActionButton :value="props.checkPayTo" copy-key="checkPayToR" :copied-key="props.copiedKey"
                :copy-to-clipboard="props.copyToClipboard" />
        </div>

        <div :class="contentGridClass">
            <div
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Mail To</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5 whitespace-pre-line">{{ props.checkMailTo }}</p>
                </div>
                <CopyActionButton :value="props.checkMailTo" copy-key="checkMailToR"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>

            <div
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3"
                :class="{ 'opacity-50': !props.zelleReference }">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Amount</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5 amount-number">{{ props.zelleReference ?
                        `$${props.currencyFormat(props.totalAmount)}` : 'Select invoices above' }}</p>
                </div>
                <CopyActionButton v-if="props.zelleReference" :value="`$${props.currencyFormat(props.totalAmount)}`"
                    copy-key="checkAmountR" :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>
        </div>

        <div :class="contentGridClass">
            <div
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3"
                :class="{ 'opacity-50': !props.zelleReference }">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Memo</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5 tracking-wider">{{
                        props.zelleReference || 'Select invoices above' }}</p>
                </div>
                <CopyActionButton v-if="props.zelleReference" :value="props.zelleReference" copy-key="checkMemoR"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>
            <div v-if="props.layout !== 'sidebar'" class="hidden md:block"></div>
        </div>

        <!-- Already sent / upload prompt -->
        <!-- <div
            class="rounded-lg border border-theme-border border-opacity-40 bg-theme-bg-shade bg-opacity-10 px-3 py-2.5 text-xs text-theme-body leading-relaxed">
            <span class="font-semibold text-theme-secondary">Already sent it?</span>
            Tap below to ping our AR team — we'll match it to your order.
        </div> -->

        <!-- Upload panel -->
        <template v-if="showUpload">

            <!-- Success state -->
            <div v-if="uploadSuccess"
                class="rounded-lg border border-green-200 bg-green-50 px-3 py-3 flex items-start gap-3">
                <div class="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-600" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                        stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </div>
                <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-green-700">Check received!</p>
                    <p class="text-xs text-green-600 mt-0.5">We'll match it to your order and apply your payment
                        shortly.</p>
                </div>
                <button @click="resetUpload" class="text-xs text-green-500 hover:text-green-700 flex-shrink-0 mt-0.5">
                    Upload another
                </button>
            </div>

            <!-- Upload form -->
            <div v-else
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-3 space-y-3">
                <div class="flex items-center justify-between gap-2">
                    <p class="text-xs font-semibold text-theme-secondary">Upload a photo of your check</p>
                    <button @click="resetUpload"
                        class="text-theme-body hover:text-theme-secondary transition-colors flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <!-- File not yet selected -->
                <div v-if="!uploadedFile" class="grid grid-cols-2 gap-2">
                    <!-- Desktop: QR code tile -->
                    <!-- <div v-if="!isMobile"
                        class="flex flex-col items-center justify-center gap-2 p-3 rounded-lg border border-theme-border border-opacity-30 bg-theme-bg-shade bg-opacity-10 text-center">
                        <QrcodeVue :value="checkQrUrl" :size="72" level="M" />
                        <div>
                            <p class="text-xs font-semibold text-theme-secondary">Scan with phone</p>
                            <p class="text-tiny text-theme-body mt-0.5">Use your phone camera to upload</p>
                        </div>
                    </div> -->

                    <!-- Mobile: camera tile -->
                    <button v-if="isMobile" type="button" @click="fileInput.click()"
                        class="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border border-theme-border border-opacity-30 bg-theme-bg-shade bg-opacity-10 hover:bg-opacity-20 transition-colors text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7 text-theme-body" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"
                            stroke-linejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                        </svg>
                        <div>
                            <p class="text-xs font-semibold text-theme-secondary">Take a photo</p>
                            <p class="text-tiny text-theme-body mt-0.5">Open camera</p>
                        </div>
                    </button>

                    <!-- Always: from library / choose file -->
                    <button type="button" @click="fileInput.click()"
                        class="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border border-theme-border border-opacity-30 bg-theme-bg-shade bg-opacity-10 hover:bg-opacity-20 transition-colors text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7 text-theme-body" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"
                            stroke-linejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                        </svg>
                        <div>
                            <p class="text-xs font-semibold text-theme-secondary">{{ isMobile ? 'From library' : 'Choose file' }}</p>
                            <p class="text-tiny text-theme-body mt-0.5">Image or PDF</p>
                        </div>
                    </button>
                </div>

                <!-- File selected — preview row + confirm -->
                <div v-else class="space-y-2">
                    <div
                        class="flex items-center gap-2.5 px-3 py-2 rounded-md border border-theme-border border-opacity-30 bg-theme-bg-shade bg-opacity-20">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-theme-body flex-shrink-0"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <p class="text-xs text-theme-secondary truncate flex-1">{{ uploadedFile.name }}</p>
                        <button @click="clearFile"
                            class="text-theme-body hover:text-theme-secondary transition-colors flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <button type="button" @click="confirmUpload" :disabled="uploadLoading"
                        class="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-xs font-semibold transition-all duration-150"
                        :class="uploadLoading ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'"
                        style="background-color: #2B303F;">
                        <template v-if="uploadLoading">
                            <svg class="animate-spin w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none"
                                viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4" />
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                            Uploading…
                        </template>
                        <template v-else>
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                stroke-linejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Confirm Upload
                        </template>
                    </button>
                </div>

                <!-- Error -->
                <p v-if="uploadError" class="text-xs text-red-500">{{ uploadError }}</p>

                <!-- Email fallback -->
                <p class="text-tiny text-theme-body leading-relaxed">
                    Having trouble?
                    <a v-if="props.invoiceEmail" :href="`mailto:${props.invoiceEmail}`"
                        class="text-theme-secondary font-medium underline">Email it to us</a>
                    <span v-else class="text-theme-secondary font-medium">Email it to us</span>
                    instead.
                </p>
            </div>

            <!-- Hidden file input: mobile uses camera capture, desktop uses standard picker -->
            <input v-if="isMobile" ref="fileInput" type="file" accept="image/*" capture="environment" class="hidden"
                @change="onFileSelected" />
            <input v-else ref="fileInput" type="file" accept="image/*,application/pdf" class="hidden"
                @change="onFileSelected" />
        </template>

        <div class="flex items-center gap-2 flex-wrap pt-3 mt-1 border-t border-theme-border border-opacity-40">
            <!-- <button type="button" @click="showUpload = !showUpload"
                class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-theme-secondary text-white text-xs font-semibold hover:bg-opacity-95 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload check photo
            </button> -->
            <button type="button" @click="copyInstructions"
                class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-theme-border border-opacity-60 bg-white text-theme-secondary text-xs font-semibold hover:bg-theme-bg-shade hover:bg-opacity-20 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                {{ instructionsCopied ? 'Copied!' : 'Copy instructions' }}
            </button>
        </div>
    </div>
</template>
